import React from "react";
import { FieldOverrides, OverrideField, VisitDetails } from "@/common/lib/api/visitMerges";
import { useAllDoctors } from "@/state/hooks/useDoctors";
import { useAllResources } from "@/state/hooks/useResources";
import { TOUCH_MIN_H } from "@/common/utils/touchTarget";
import { COPY, formatTime } from "./copy";

/** hasRooms: the clinic has rooms at all; without any, "No room" is noise. */
export type DetailNames = {
    doctor: (id: string | null) => string;
    resource: (id: string | null) => string;
    hasRooms: boolean;
};

type Field = {
    field: OverrideField;
    label: string;
    show: (v: VisitDetails, n: DetailNames) => string;
    /** A field the kept visit leaves empty while the other visit has it, so the merge takes it by default. */
    isSet?: (v: VisitDetails) => boolean;
};
const FIELDS: Field[] = [
    { field: "scheduled_at", label: "Time", show: (v) => formatTime(v.scheduledAt, v.createdAt) },
    {
        field: "appointment_duration",
        label: "Length",
        show: (v) => (v.appointmentDuration ? `${v.appointmentDuration} min` : COPY.noLength),
        isSet: (v) => !!v.appointmentDuration,
    },
    { field: "type", label: "Type", show: (v) => v.type || COPY.noType, isSet: (v) => !!v.type },
    { field: "resource_id", label: "Room", show: (v, n) => n.resource(v.resourceId), isSet: (v) => !!v.resourceId },
    { field: "doctor_id", label: "Doctor", show: (v, n) => n.doctor(v.doctorId), isSet: (v) => !!v.doctorId },
];

/** The server refuses Room onto a visit with its own room rows. */
const offeredFields = (kept: VisitDetails) =>
    kept.hasOwnRooms ? FIELDS.filter((f) => f.field !== "resource_id") : FIELDS;

/**
 * Each offered field the kept visit leaves empty and the other visit fills is taken from the other visit.
 * None for a linked kept visit: sync owns its details and the server refuses overrides for it.
 */
export function suggestedOverrides(kept: VisitDetails, emptied: VisitDetails): FieldOverrides {
    const out: FieldOverrides = {};
    if (kept.linked) return out;
    offeredFields(kept).forEach(({ field, isSet }) => {
        if (isSet && !isSet(kept) && isSet(emptied)) out[field] = true;
    });
    return out;
}

export function useDetailNames(): DetailNames {
    const { doctors } = useAllDoctors();
    const { data: resources = [] } = useAllResources();
    return {
        // A set id that is not in the list (deleted, still loading, failed to load) is not "none".
        doctor: (id) => (id ? (doctors.find((d) => d.id === id)?.doctor_name ?? COPY.unknownDoctor) : COPY.noDoctor),
        resource: (id) => (id ? (resources.find((r) => r.id === id)?.resource_name ?? COPY.unknownRoom) : COPY.noRoom),
        hasRooms: resources.length > 0,
    };
}

export function summarizeKept(
    kept: VisitDetails,
    emptied: VisitDetails,
    overrides: FieldOverrides,
    names: DetailNames,
): string {
    // Room shows whenever either visit has one, so a room change is never left out of the summary.
    const showRoom = names.hasRooms || !!kept.resourceId || !!emptied.resourceId;
    return FIELDS.filter((f) => f.field !== "resource_id" || showRoom)
        .map(({ field, show }) => show(overrides[field] ? emptied : kept, names))
        .join(" · ");
}

type Props = {
    kept: VisitDetails;
    emptied: VisitDetails;
    overrides: FieldOverrides;
    onChange: (o: FieldOverrides) => void;
    onDone: () => void;
};

export function KeptDetailsOverrides({ kept, emptied, overrides, onChange, onDone }: Props) {
    const names = useDetailNames();
    const fields = offeredFields(kept);
    const keptTime = formatTime(kept.scheduledAt, kept.createdAt);
    const emptiedTime = formatTime(emptied.scheduledAt, emptied.createdAt);
    const set = (field: OverrideField, fromEmptied: boolean) => {
        const next = { ...overrides };
        if (fromEmptied) next[field] = true;
        else delete next[field];
        onChange(next);
    };
    return (
        <div className="flex flex-col gap-2">
            <div className="overflow-hidden rounded-lg border border-gray-200">
                <div className="flex items-center justify-between border-b border-gray-200 bg-slate-50 px-3 py-2">
                    <span className="text-sm font-bold">{COPY.overridesTitle}</span>
                    <span className="flex items-center gap-3">
                        <button
                            type="button"
                            className={`text-sm font-semibold text-max-900 ${TOUCH_MIN_H}`}
                            onClick={() => onChange(suggestedOverrides(kept, emptied))}
                        >
                            {COPY.resetToSuggested}
                        </button>
                        <button
                            type="button"
                            className={`rounded-md px-2.5 py-1 text-sm font-semibold ring-1 ring-inset ring-gray-300 ${TOUCH_MIN_H}`}
                            onClick={onDone}
                        >
                            {COPY.done}
                        </button>
                    </span>
                </div>
                <div
                    aria-hidden="true"
                    className="grid grid-cols-[96px_1fr_1fr] gap-1 border-b border-gray-100 px-2 py-1.5 text-xs font-semibold text-gray-500"
                >
                    <span />
                    <span className="px-2">{COPY.overridesFromColumn(emptiedTime)}</span>
                    <span className="px-2">{COPY.overridesKeepColumn(keptTime)}</span>
                </div>
                {fields.map(({ field, label, show }) => {
                    const a = show(emptied, names);
                    const b = show(kept, names);
                    if (a === b) {
                        return (
                            <div
                                key={field}
                                className="grid grid-cols-[96px_1fr] border-b border-gray-100 px-2 py-2 text-sm last:border-0"
                            >
                                <span className="text-gray-500">{label}</span>
                                <span className="text-gray-500">{`${b} on both`}</span>
                            </div>
                        );
                    }
                    const fromEmptied = !!overrides[field];
                    return (
                        <div
                            key={field}
                            role="radiogroup"
                            aria-label={label}
                            className="grid grid-cols-[96px_1fr_1fr] items-center gap-1 border-b border-gray-100 px-2 py-1 text-sm last:border-0"
                        >
                            <span className="text-gray-500">{label}</span>
                            {[
                                { val: a, on: fromEmptied, pick: true, time: emptiedTime },
                                { val: b, on: !fromEmptied, pick: false, time: keptTime },
                            ].map((opt) => (
                                <label
                                    key={String(opt.pick)}
                                    className={`flex items-center gap-2 rounded-md border px-2 py-1.5 ${opt.on ? "border-max-200 bg-max-25 font-semibold" : "border-transparent"}`}
                                >
                                    <input
                                        type="radio"
                                        name={`ov-${field}`}
                                        className="h-4 w-4 accent-max-900"
                                        checked={opt.on}
                                        onChange={() => set(field, opt.pick)}
                                        aria-label={COPY.overrideOption(label, opt.val, opt.time)}
                                    />
                                    {opt.val}
                                    {opt.on && opt.pick && (
                                        <span className="ml-auto text-[11px] font-bold text-tropicana-800">
                                            {COPY.changed}
                                        </span>
                                    )}
                                </label>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
